import React, { useState } from "react";

import { Loader2, Truck } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toast } from "~/hooks/use-toast";
import { useTransportistas } from "~/hooks/useTransportistas";
import { assignTransportista } from "~/services/transportistaService";

interface TransportistaAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesRequestId: string;
  onAssignmentComplete: () => void;
  onGenerateFactura: () => void;
}

export const TransportistaAssignmentDialog: React.FC<
  TransportistaAssignmentDialogProps
> = ({
  open,
  onOpenChange,
  salesRequestId,
  onAssignmentComplete,
  onGenerateFactura,
}) => {
  const [selectedTransportistaId, setSelectedTransportistaId] =
    useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);
  const { transportistas, isLoading } = useTransportistas();

  const handleAssignAndGenerate = async () => {
    setIsAssigning(true);
    try {
      // Only assign transportista if one is selected
      if (selectedTransportistaId) {
        await assignTransportista(salesRequestId, selectedTransportistaId);

        toast({
          title: "Transportista asignado",
          description: "El transportista ha sido asignado exitosamente",
          variant: "default",
        });

        // Call the assignment complete callback
        onAssignmentComplete();
      }

      // Generate the factura regardless of transportista assignment
      onGenerateFactura();

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Error in assignment and generation process:", error);
      toast({
        title: "Error",
        description: selectedTransportistaId
          ? "No se pudo asignar el transportista"
          : "No se pudo generar la factura",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCancel = () => {
    setSelectedTransportistaId("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Asignar Transportista
          </DialogTitle>
          <DialogDescription>
            Selecciona un transportista para esta nota de venta (opcional).
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label htmlFor="transportista" className="text-sm font-medium">
              Transportista <span className="text-gray-400">(opcional)</span>
            </label>
            {isLoading ? (
              <div className="flex items-center gap-2 rounded-md border bg-gray-50 p-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">
                  Cargando transportistas...
                </span>
              </div>
            ) : (
              <Select
                value={selectedTransportistaId}
                onValueChange={setSelectedTransportistaId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un transportista" />
                </SelectTrigger>
                <SelectContent>
                  {transportistas.length > 0 ? (
                    transportistas.map((transportista) => (
                      <SelectItem
                        key={transportista.id}
                        value={transportista.id}
                      >
                        {`${transportista.name} ${transportista.lastname}`}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No hay transportistas disponibles
                    </div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-col sm:justify-between">
          <div className="text-sm text-muted-foreground">
            💡 Tip: Puedes generar la factura ahora y asignar el transportista
            después
          </div>
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isAssigning}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAssignAndGenerate}
              disabled={isAssigning || isLoading}
              className="min-w-[200px]"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {selectedTransportistaId ? "Asignando..." : "Generando..."}
                </>
              ) : selectedTransportistaId ? (
                "Asignar y Generar Factura"
              ) : (
                "Generar Factura"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
